import React, { useEffect, useRef, useState } from "react";
import { Box, Flex, Input, Icon, Text } from "@chakra-ui/react";
import { HiOutlinePlus } from "react-icons/hi2";
import { c } from "../classroomColors";

/**
 * Add-in-place. A quiet "+ Add a lesson" row that becomes a field on click and
 * stays open after each save, so a teacher laying out a course types
 * name-enter-name-enter instead of opening a dialog once per item.
 */
const InlineAdd = ({ label, placeholder, onAdd, indent = 0, size = "sm" }) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Enter and blur both commit, and pressing Enter causes a blur - so the
  // name is read from the input itself and cleared before the await, rather
  // than from state a second handler would still see the old value of.
  const inFlight = useRef(false);

  const commit = async () => {
    if (inFlight.current) return;
    const name = (inputRef.current?.value || "").trim();
    if (!name) {
      setOpen(false);
      return;
    }
    inFlight.current = true;
    if (inputRef.current) inputRef.current.value = "";
    setValue("");
    setSaving(true);
    try {
      await onAdd(name);
      // Stays open: the next lesson is almost always the next thing typed.
      inputRef.current?.focus();
    } finally {
      setSaving(false);
      inFlight.current = false;
    }
  };

  const big = size === "md";

  if (!open) {
    return (
      <Flex
        as="button"
        onClick={() => setOpen(true)}
        align="center"
        gap="8px"
        width="100%"
        pl={`${12 + indent}px`}
        pr="12px"
        py={big ? "10px" : "8px"}
        borderRadius="9px"
        color={c.textMuted}
        transition="background 0.15s, color 0.15s"
        _hover={{ bg: c.hover, color: c.text }}
      >
        <Icon as={HiOutlinePlus} boxSize={big ? "15px" : "13px"} />
        <Text fontSize={big ? "13.5px" : "12.5px"} fontWeight="400">
          {label}
        </Text>
      </Flex>
    );
  }

  return (
    <Box pl={`${indent}px`} pr="2px" py="2px">
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
          if (e.key === "Escape") {
            setValue("");
            setOpen(false);
          }
        }}
        onBlur={commit}
        isDisabled={saving}
        placeholder={placeholder}
        height={big ? "38px" : "34px"}
        fontSize={big ? "13.5px" : "12.5px"}
        borderRadius="9px"
        bg={c.inputBg}
        border="1px solid"
        borderColor={c.accent}
        color={c.text}
        _placeholder={{ color: c.textMuted }}
        _focus={{ borderColor: c.accent, boxShadow: "none" }}
      />
    </Box>
  );
};

export default InlineAdd;
